import { lazy, Suspense } from 'react'

import { createFileRoute, retainSearchParams } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { graylogQueries } from '@/features/graylog/queries'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'
import { TableFullSkeleton } from '@/shared/data-table/components/table-skeleton'

import { ErrorBoundary } from '@/components/common/error-boundary'

const AutomationSettingsTable = lazy(
	() => import('@/features/graylog/automation-settings/component/table'),
)

const searchSchema = z.object({
	q: z.string().optional(),
})

export const Route = createFileRoute(
	'/_dashboard/graylog/automation-settings/',
)({
	validateSearch: zodValidator(searchSchema),

	search: {
		middlewares: [retainSearchParams(['q'])],
	},

	beforeLoad: () => roleAuthGuard(ROLES.Analyst),

	loader: async ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant() as string

		// Preload envent sources
		if (tenant) {
			queryClient.prefetchQuery(graylogQueries.eventSources(tenant))

			// Ensure the automation settings are loaded
			await queryClient.ensureInfiniteQueryData(
				graylogQueries.automationSettings(tenant),
			)
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Automation Settings | Graylog | BitLyft AIR®',
			},
		],
	}),

	component: AutomationSettingsRoute,
	pendingComponent: AutomationSettingsFallback,
	errorComponent: RouteErrorComponent,
})

function AutomationSettingsRoute() {
	return (
		<ErrorBoundary>
			<Suspense fallback={<TableFullSkeleton withLastUpdated rows={5} />}>
				<AutomationSettingsTable />
			</Suspense>
		</ErrorBoundary>
	)
}

function AutomationSettingsFallback() {
	return (
		<div className="flex flex-col gap-4">
			<TableFullSkeleton withLastUpdated rows={5} />
		</div>
	)
}
